// src/utils/pidStatus.js
//
// Single source of truth for the Package Index (PID) lifecycle status: the ordered
// label set, a sort rank, and per-status colors. Mirrors specStatus.js but with the
// PID status set (Active / Incoming / Pending / Inactive) and consumes the shared
// var(--color-status-*) tokens from styles/tokens.js (never raw literals).
//
// Only `Active` shows on the read-only Package Index browse (the browse fetches with
// ?activeOnly=1). Incoming / Pending / Inactive are editor-facing hidden states.
// Colors pair a background token with a legible foreground: white on the darker
// green/red, near-black on the orange/yellow (white on those fails WCAG AA).

export const PID_STATUSES = ['Active', 'Incoming', 'Pending', 'Inactive'];

// The default when creating a new PID entry (Add panels) — staged/hidden until activated.
export const DEFAULT_PID_STATUS = 'Pending';

// Statuses a Customer Specs picker may OFFER for a PID-backed reference (Label ID + UPC,
// and the PLUs that follow from labels): live items plus ones coming soon. Pending and
// Inactive PID records are not offered for new selections when building/editing a spec.
// An already-selected reference still renders (the editor pools are add-lists only;
// TieredGradeList renders selected values independently of the option pool, and the UPC
// resolution list is unfiltered). See [[customer-specs]] (label-picker curation).
export const SPEC_SELECTABLE_PID_STATUSES = ['Active', 'Incoming'];

export function isSpecSelectablePidStatus(value) {
  return SPEC_SELECTABLE_PID_STATUSES.includes(normalizePidStatus(value));
}

const STATUS_STYLE = {
  'Active':   { bg: 'var(--color-status-active)',   fg: 'var(--white)' },
  'Incoming': { bg: 'var(--color-status-incoming)', fg: 'var(--color-text)' },
  'Pending':  { bg: 'var(--color-status-pending)',  fg: 'var(--color-text)' },
  'Inactive': { bg: 'var(--color-status-inactive)', fg: 'var(--white)' },
};

// Normalize a stored/raw value to a known label. Unknown or empty → 'Active',
// matching the server's read-time ISNULL(Status,'Active') fail-open behavior.
export function normalizePidStatus(value) {
  const s = String(value == null ? '' : value).trim();
  return PID_STATUSES.includes(s) ? s : 'Active';
}

// { bg, fg } CSS custom-property tokens for a status chip.
export function pidStatusColor(label) {
  return STATUS_STYLE[normalizePidStatus(label)];
}

// Lifecycle order for sorting (Active first → Inactive last).
export function pidStatusRank(label) {
  return PID_STATUSES.indexOf(normalizePidStatus(label));
}

// Resolve the lifecycle status of a browse card / group / row. Handles both shapes the
// Package Index browse produces: a flat row or group carrying a top-level `.Status`
// (Methods/PLU/UPC/Displays rows, and Bags groups — the server sets it per LABEL_ID), and
// a client-grouped card whose status lives on its first item (`.Items[0].Status` —
// Methods/PLU cards, grouped in PackageIndexPage). Falls back to 'Active', matching the
// server's ISNULL(Status,'Active') coalesce.
export function resolveCardStatus(obj) {
  if (!obj) return 'Active';
  const raw = obj.Status != null ? obj.Status : obj.Items?.[0]?.Status;
  return normalizePidStatus(raw);
}
